/**
 * Eligibility cache — persists pool-eligibility state per (chainId, contract)
 * in localStorage so we only scan NEW events on each visit.
 *
 * Structure stored:
 *  {
 *    version: 1,
 *    chainId, contract,
 *    lastScannedBlock: N,             // last block fully scanned (inclusive)
 *    userStates: {                    // latest known state per user/pool
 *      [userId]: { 1: bool, 2: bool, 3: bool }
 *    },
 *    savedAt: timestamp
 *  }
 *
 * Counts at runtime = (Σ userStates[*].poolType==true) + 1 for default referrer.
 *
 * Storage size: ~30 bytes/user × users-who-ever-flipped → 50k users = ~1.5MB,
 * well within localStorage's 5–10MB limit.
 */

const VERSION = 1;
const PREFIX = 'galaxyxon-elig-cache';

export const cacheKey = (chainId, contractAddr) => {
  if (!contractAddr) return null;
  return `${PREFIX}-v${VERSION}-${chainId}-${contractAddr.toLowerCase()}`;
};

export const loadCache = (key) => {
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== VERSION) return null;
    if (typeof parsed.lastScannedBlock !== 'number') return null;
    if (!parsed.userStates || typeof parsed.userStates !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

export const saveCache = (key, payload) => {
  if (!key || typeof window === 'undefined') return false;
  try {
    const out = {
      version: VERSION,
      chainId: payload.chainId,
      contract: payload.contract,
      lastScannedBlock: payload.lastScannedBlock,
      userStates: payload.userStates,
      savedAt: Date.now()
    };
    window.localStorage.setItem(key, JSON.stringify(out));
    return true;
  } catch {
    return false;
  }
};

export const clearCache = (key) => {
  if (!key || typeof window === 'undefined') return;
  try { window.localStorage.removeItem(key); } catch {}
};

/**
 * Apply a list of PoolEligibilityUpdated events to a userStates object.
 * Mutates and returns userStates. Events are assumed to be in chronological order.
 */
export const applyEligibilityEvents = (userStates, events) => {
  for (const ev of events) {
    if (!ev?.args) continue;
    const userId = ev.args.userId.toString();
    const poolType = Number(ev.args.poolType);
    const eligible = !!ev.args.eligible;

    if (!userStates[userId]) userStates[userId] = {};
    userStates[userId][poolType] = eligible;
  }
  return userStates;
};

/**
 * Tally counts across userStates. Adds +1 each pool for default referrer
 * (set in CoreStorage constructor, never emits event).
 */
export const tallyCounts = (userStates) => {
  const counts = { 1: 1, 2: 1, 3: 1 };  // default referrer baseline
  for (const userId in userStates) {
    const u = userStates[userId];
    for (const pt of [1, 2, 3]) {
      if (u[pt] === true) counts[pt] += 1;
    }
  }
  return counts;
};

export const fmtCacheAge = (savedAt) => {
  if (!savedAt) return '—';
  const sec = Math.max(0, Math.floor((Date.now() - savedAt) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
};
